import { useState } from 'react';
import { Link } from 'react-router-dom';
import { StatusChip } from '../../components/data';
import { DeleteButton, ErrorBanner, Field, Loading, Modal } from '../../components/ui';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type { Band } from '../../lib/format';
import type { EosMeetingRow, MeetingStatus } from './eosTypes';
import { EosSection, eosToday } from './eosUi';

const STATUS_LABELS: Record<MeetingStatus, string> = {
  scheduled: 'Scheduled',
  in_progress: 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

/**
 * The rating band on a completed meeting.
 *
 * An L10 is supposed to score 8 or better — that is where the name comes from.
 * Below 8 is the signal that the cadence itself needs a conversation, so the
 * bands are set against that bar rather than against a generic out-of-ten.
 */
function ratingBand(rating: number | null): Band {
  if (rating === null) return 'neutral';
  if (rating >= 8) return 'good';
  if (rating >= 6) return 'warning';
  return 'serious';
}

/**
 * Meetings — schedule an L10, and open the runner.
 *
 * Deliberately thin. The list is a way in to the runner, which is where the
 * meeting actually happens; putting the agenda on this page as well would give
 * two places to run the same meeting from.
 */
export function EosMeetings({ teamId, onChanged }: { teamId: number; onChanged: () => void }) {
  const meetings = useResource<EosMeetingRow[]>(`/eos/meetings?teamId=${teamId}`);
  const [scheduling, setScheduling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rows = meetings.data ?? [];
  const upcoming = rows.filter((m) => m.Status === 'scheduled' || m.Status === 'in_progress');
  const past = rows.filter((m) => m.Status === 'completed' || m.Status === 'cancelled');

  return (
    <>
      {error && <ErrorBanner message={error} />}

      <EosSection
        title="Meetings"
        note="The weekly Level 10. Opening one starts the timed agenda — Segue through to Conclude."
        actions={
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setScheduling(true)}>
            Schedule meeting
          </button>
        }
      >
        {meetings.loading && <Loading label="Loading meetings" />}
        {meetings.error && <ErrorBanner message={meetings.error} />}

        {upcoming.length === 0 && !meetings.loading && (
          <p className="muted">Nothing scheduled. Schedule this week's L10 to get started.</p>
        )}

        {upcoming.map((meeting) => (
          <div key={meeting.MeetingId} className="eos-meeting">
            <div className="grow">
              <span className="strong">{meeting.Title}</span>
              <div className="xs subtle mt-2">
                {formatDate(meeting.ScheduledDate)}
                {/* The slot time, when one is set. A standing weekly L10 is
                    booked at an hour, and two of the imported ones sit at a
                    different hour from the managers' meeting — see migration
                    059. */}
                {meeting.ScheduledTime && ` at ${meeting.ScheduledTime}`} · {meeting.AttendeeCount}{' '}
                {meeting.AttendeeCount === 1 ? 'person' : 'people'} invited
              </div>
            </div>
            <div className="row wrap" style={{ gap: 4 }}>
              <StatusChip band={meeting.Status === 'in_progress' ? 'warning' : 'neutral'}>
                {STATUS_LABELS[meeting.Status]}
              </StatusChip>
              <Link className="btn btn-primary btn-sm" to={`/eos/meetings/${meeting.MeetingId}`}>
                {meeting.Status === 'in_progress' ? 'Rejoin' : 'Start'}
              </Link>
              <DeleteButton
                what={`the meeting on ${formatDate(meeting.ScheduledDate)}`}
                onConfirm={() => {
                  void api
                    .del(`/eos/meetings/${meeting.MeetingId}`)
                    .then(() => {
                      meetings.reload();
                      onChanged();
                    })
                    .catch((err: unknown) =>
                      setError(err instanceof Error ? err.message : 'Could not delete it.'),
                    );
                }}
              />
            </div>
          </div>
        ))}
      </EosSection>

      {past.length > 0 && (
        <EosSection title="History" note="What each meeting produced, and how the room rated it.">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Meeting</th>
                  <th scope="col" className="tabular">
                    Rating
                  </th>
                  <th scope="col" className="tabular">
                    Solved
                  </th>
                  <th scope="col" className="tabular">
                    To-dos
                  </th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {past.map((meeting) => (
                  <tr key={meeting.MeetingId}>
                    <td>{formatDate(meeting.ScheduledDate)}</td>
                    <th scope="row">{meeting.Title}</th>
                    <td>
                      {meeting.AverageRating === null ? (
                        <span className="subtle">—</span>
                      ) : (
                        <StatusChip band={ratingBand(Number(meeting.AverageRating))}>
                          {Number(meeting.AverageRating).toFixed(1)}
                        </StatusChip>
                      )}
                    </td>
                    <td className="tabular">{meeting.SolvedCount}</td>
                    <td className="tabular">{meeting.TodoCount}</td>
                    <td>
                      <Link className="btn btn-ghost btn-sm" to={`/eos/meetings/${meeting.MeetingId}`}>
                        Recap
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </EosSection>
      )}

      {scheduling && (
        <ScheduleDialog
          teamId={teamId}
          onClose={() => setScheduling(false)}
          onSaved={() => {
            setScheduling(false);
            meetings.reload();
            onChanged();
          }}
        />
      )}
    </>
  );
}

function ScheduleDialog({
  teamId,
  onClose,
  onSaved,
}: {
  teamId: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [scheduledDate, setDate] = useState(eosToday());
  const [title, setTitle] = useState('Level 10 Meeting');
  const [meetingType, setType] = useState<'level10' | 'quarterly' | 'annual'>('level10');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post('/eos/meetings', { teamId, scheduledDate, title: title.trim(), meetingType });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not schedule it.');
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Schedule meeting"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void submit()}>
            {saving ? 'Scheduling…' : 'Schedule'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <p className="xs subtle">Everybody on the team is invited automatically.</p>
      <div className="form-grid mt-4">
        <Field label="Date">
          <input
            className="input"
            type="date"
            value={scheduledDate}
            onChange={(e) => setDate(e.target.value)}
          />
        </Field>
        <Field label="Title">
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="Type" hint="The agenda is the same; the type records which cadence it belongs to.">
          <select
            className="select"
            value={meetingType}
            onChange={(e) => setType(e.target.value as 'level10' | 'quarterly' | 'annual')}
          >
            <option value="level10">Level 10 (weekly)</option>
            <option value="quarterly">Quarterly</option>
            <option value="annual">Annual</option>
          </select>
        </Field>
      </div>
    </Modal>
  );
}
